import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import PickerMenu from './PickerMenu';
import OrgAccessCard, { LockedOrgCard } from './OrgAccessCard';
import SectionHead from './SectionHead';
import { apiErrorMessage, getUser, isAbortError, listUsers } from './usersApi';
import { blankOrgEntry, copyAccessFrom, displayName } from './userModel';

/** "Copy access from…" — search another user and copy their orgAccess into the draft. */
function CopyAccessMenu({ excludeId, onCopy, disabled }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ loading: false, options: [] });
  const ctrl = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      ctrl.current?.abort();
      const c = new AbortController();
      ctrl.current = c;
      setState((s) => ({ ...s, loading: true }));
      listUsers({ status: 'all', search: q.trim() || undefined, limit: 8 }, { signal: c.signal })
        .then((res) => {
          if (c.signal.aborted) return;
          setState({
            loading: false,
            options: res.users.filter((u) => u.id !== excludeId).map((u) => ({ value: u.id, label: displayName(u), hint: u.email })),
          });
        })
        .catch((err) => { if (!isAbortError(err)) setState({ loading: false, options: [] }); });
    }, 250);
    return () => clearTimeout(t);
  }, [q, excludeId, open]);

  useEffect(() => () => ctrl.current?.abort(), []);

  const pick = async (id) => {
    try {
      const source = await getUser(id);
      onCopy(source);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't load that user's access"));
    }
  };

  return (
    <PickerMenu
      triggerClassName="ak-btn ak-small"
      align="end"
      disabled={disabled}
      searchPlaceholder="Search users"
      emptyText="No users found"
      loading={state.loading}
      options={state.options}
      onQueryChange={setQ}
      onOpenChange={setOpen}
      onPick={pick}
    >
      Copy access from…
    </PickerMenu>
  );
}

/** Organization access section of the single-page user panel. */
export default function AccessSection({ user, draft, setDraft, directory, errors, disabled }) {
  const access = draft.orgAccess || {};
  const resolved = user?.adminMeta?.reportGroups || {};
  const nameOf = (code) => directory.byCode.get(code)?.name || directory.orgNameByCode[code]
    || user?.adminMeta?.orgs?.find((o) => o.code === code)?.name || code;
  const codes = Object.keys(access).sort((a, b) => {
    const ma = directory.manageable.has(a) ? 0 : 1;
    const mb = directory.manageable.has(b) ? 0 : 1;
    return ma - mb || nameOf(a).localeCompare(nameOf(b));
  });
  const addable = directory.orgs.filter((o) => !access[o.code]);

  const setEntry = (code, entry) => setDraft((d) => ({ ...d, orgAccess: { ...d.orgAccess, [code]: entry } }));
  const remove = (code) => setDraft((d) => {
    const next = { ...d.orgAccess };
    delete next[code];
    return { ...d, orgAccess: next };
  });
  const add = (code) => setDraft((d) => ({ ...d, orgAccess: { ...d.orgAccess, [code]: blankOrgEntry('') } }));
  const copy = (source) => {
    setDraft((d) => ({ ...d, orgAccess: copyAccessFrom(d.orgAccess, source.orgAccess, directory.manageable) }));
    toast.success(`Copied access from ${displayName(source)} — review, then save`);
  };

  // One org: always open. Several: an org starts open when it's new to the
  // draft (just added / copied) or has a validation error; the rest collapse
  // to a one-line summary.
  const startOpen = (code) => codes.length <= 1 || !user?.orgAccess?.[code] || Boolean(errors[`role:${code}`]);

  return (
    <section className="us-section" aria-labelledby="us-sec-access">
      <SectionHead id="us-sec-access" title={`Organization access${codes.length > 1 ? ` · ${codes.length}` : ''}`}>
        <CopyAccessMenu excludeId={draft.id} onCopy={copy} disabled={disabled} />
      </SectionHead>

      {errors.orgAccess ? (
        <div className="ak-field-error" role="alert" data-error-key="orgAccess" tabIndex={-1}>{errors.orgAccess}</div>
      ) : null}

      {codes.map((code) => (directory.manageable.has(code) ? (
        <OrgAccessCard
          key={code}
          code={code}
          org={directory.byCode.get(code) || { code, name: nameOf(code), id: null }}
          entry={access[code]}
          resolvedGroups={Object.prototype.hasOwnProperty.call(resolved, code) ? resolved[code] : undefined}
          locations={directory.locationsForOrg(code)}
          roleError={errors[`role:${code}`]}
          defaultOpen={startOpen(code)}
          disabled={disabled}
          onChange={(entry) => setEntry(code, entry)}
          onRemove={() => remove(code)}
        />
      ) : (
        <LockedOrgCard key={code} name={nameOf(code)} entry={access[code]} />
      )))}

      <PickerMenu
        triggerClassName="ak-dashed"
        disabled={disabled || !addable.length}
        searchPlaceholder="Search organizations"
        emptyText="No more organizations"
        options={addable.map((o) => ({ value: o.code, label: o.name, hint: o.code }))}
        onPick={add}
      >
        {addable.length || directory.loading ? '+ Add organization' : 'Already in every organization you manage'}
      </PickerMenu>
    </section>
  );
}
